import styled, { css } from 'styled-components';

import { ACQ, CHANNEL_CARD_TONE, COLORS, GRADIENTS, MOBILE } from '../../theme/tokens';
import type { ChannelState } from '../../theme/tokens';
import { Button, Text } from '../../components';

// --- Top row group: CPA card + budget summary + date disclaimer -------------
// One grouping container (the acquisition-root ChannelsBudgets pattern): the two
// cards sit inside it as lifted tiles, with a shared date disclaimer footer. On
// the page cardOuter is a touch lighter than the black backdrop; in light theme
// it derives to the grey container the white cards rest on.
export const TopGroup = styled.div`
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 10px;
`;

// Side by side when the content column is wide, stacked when narrow. `auto-fit`
// + `minmax` reflows on the actual column width (which the layout sidebars eat
// into), not the viewport. `stretch` so both cards share a height (Figma).
export const TopRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 12px;
  align-items: stretch;
`;

// --- Date disclaimer footnote -----------------------------------------------
// Mirrors the acquisition root: the figures are yesterday's, and any limit/budget
// edits land the next day. A hairline rule lifts it off the cards, an info glyph
// anchors it, the copy stays muted so it never competes for focus.
export const Disclaimer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  /* Even rhythm around the rule: equal air above the hairline and below it to the
     copy (and the copy to the container edge, via TopGroup's 10px padding). */
  margin-top: 16px;
  padding: 16px 12px 6px;
  border-top: 1px solid ${ACQ.divider};
`;

export const DisclaimerIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  margin-top: 1px;
  color: ${ACQ.t400};
`;

export const DisclaimerText = styled(Text)`
  max-width: 660px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${ACQ.t400};
  font-variant-numeric: tabular-nums;
`;

// --- CPA advisory note (exhausted / nearing-budget states) ------------------
export const Advisory = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px;
  border-radius: 8px;
  background: ${COLORS.pageBg};
`;

export const AdvisorySpark = styled.img`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 1px;
`;

export const AdvisoryText = styled(Text)`
  // Rendered as a <p>; zero out Bootstrap Reboot's default 1rem bottom margin so
  // the copy sits evenly within the box instead of being pushed up by trailing space.
  margin: 0;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.3;
  color: ${ACQ.t300};
`;

// --- Budget summary card ----------------------------------------------------
// Same health tone as the CPA card: glow + tinted border when exhausted/nearing,
// a flat zinc surface when active.
const summaryTone = (tone: ChannelState) =>
  tone === 'active'
    ? css`
        background: ${ACQ.cardBase};
        border: 1px solid transparent;
      `
    : css`
        background: ${CHANNEL_CARD_TONE[tone].glow};
        border: 1px solid ${CHANNEL_CARD_TONE[tone].border};
      `;

export const Summary = styled.div<{ $tone: ChannelState }>`
  ${(p) => summaryTone(p.$tone)}
  border-radius: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
`;

export const SummaryTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const SummaryName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${ACQ.t50};
`;

export const Name = styled(Text)`
  font-size: 14px;
  font-weight: 700;
`;

export const BarGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const BudgetStatus = styled(Text)<{ $color: string }>`
  font-size: 12px;
  color: ${(p) => p.$color};
`;

// "Spent $X" with no denominator — the channel has no daily limit configured yet (the
// acquisition-budget API exposes none). Matches SpentOfBudget's figure styling.
export const SpentSolo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-size: 16px;
  font-weight: 700;
  color: ${ACQ.t300};
`;

/** The Figma "Manage budget" pill — dark fill, solid orange hairline, white label. */
export const ManageButton = styled(Button)`
  background: ${COLORS.ctaPillBg};
  border-color: ${COLORS.ctaPillBorder};
  color: ${ACQ.white};
  font-size: 14px;
  font-weight: 700;
  padding: 14px 12px;
`;

// --- Channel campaigns ------------------------------------------------------
// The "campaign table" container (Figma 6525:60338): a solid dark surface
// holding the campaign grid + load-more. The section title sits
// ABOVE this container (a sibling), not inside it.
export const CampaignTable = styled.div`
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 6px 6px 10px;
`;

export const CampaignList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${GRADIENTS.dividerStyledH};
`;

// Placeholder campaign card shown while the channel's campaigns load (mirrors the
// DrillCard frame: a thumbnail + title row and a row of four stat tiles).
export const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  border-radius: 16px;
  background: ${ACQ.campCard};
`;

export const SkeletonHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SkeletonTiles = styled.div`
  display: flex;
  gap: 2px;
`;

// Empty state when a channel has no campaigns yet (spans both grid columns).
export const Empty = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  text-align: center;
`;

export const EmptyText = styled(Text)`
  max-width: 360px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.4;
  color: ${ACQ.t400};
`;

export const EnabledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const EnabledLabel = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  color: ${ACQ.t400};
`;

/** Four dark stat tiles; dims as a unit when the campaign is disabled. */
export const StatTiles = styled.div<{ $muted?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 2px;
  opacity: ${(p) => (p.$muted ? 0.4 : 1)};
`;

export const StatTile = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  background: ${ACQ.cardOuter};
`;

export const StatTileLabel = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.1;
  color: ${ACQ.white};
`;

export const StatTileValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.1;
  color: ${ACQ.t50};
`;

// --- Lumio note (same featureCard surface as the metric cards) ---------------
export const Note = styled.div`
  background: ${GRADIENTS.featureCard};
  border: 0.5px solid ${CHANNEL_CARD_TONE.active.border};
  border-radius: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const NoteHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 3px 5px;
`;

export const NoteSpark = styled.img`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
`;

export const NoteTitle = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${ACQ.t300};
`;

export const NoteDesc = styled(Text)`
  padding: 0 5px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.4;
  color: ${ACQ.t300};
`;
